import { useState, FormEvent, ChangeEvent } from "react";

// Número no formato internacional (sem + e sem espaços)
const WHATSAPP_NUMBER = "5547999300330";
const WHATSAPP_DIRECT = "https://wa.me/5547999300330";

const AREAS = [
  "Direito Trabalhista",
  "Direito Civil",
  "Direito Previdenciário",
  "Direito de Família",
  "Direito Bancário",
  "Outro",
];

interface FormState {
  name: string;
  email: string;
  area: string;
  message: string;
}

const INITIAL_STATE: FormState = {
  name: "",
  email: "",
  area: "",
  message: "",
};

export default function Contato() {
  const [formData, setFormData] = useState<FormState>(INITIAL_STATE);
  const [charCount, setCharCount] = useState(0);

  // Atualiza campos de texto e textarea
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // Limita textarea a 500 caracteres
    if (name === "message") {
      if (value.length > 500) return;
      setCharCount(value.length);
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Ao submeter: monta mensagem e abre WhatsApp em nova aba
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    // Impede comportamento padrão do formulário (reload/POST)
    e.preventDefault();

    // Monta a mensagem com estrutura exata solicitada
    const text =
      `Nome Completo: ${formData.name} | ` +
      `E-mail: ${formData.email} | ` +
      `Área do Caso: ${formData.area} | ` +
      `Motivo do Contato: ${formData.message}`;

    // Codifica para URL (encodeURIComponent)
    const encoded = encodeURIComponent(text);

    // Abre WhatsApp em nova aba com a mensagem pronta
    window.open(
      `https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <section id="contato" className="bg-[#8B0F14] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-12 bg-[#E3B24A]/60" />
            <span className="text-[#E3B24A] font-body text-xs tracking-[0.3em] uppercase font-medium">
              Fale Conosco
            </span>
            <div className="h-px w-12 bg-[#E3B24A]/60" />
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-4">
            Entre em Contato
          </h2>
          <p className="font-body text-[#F2D27A]/70 text-base max-w-xl mx-auto">
            Preencha o formulário e seja direcionado ao WhatsApp com sua mensagem já pronta. Respondemos com agilidade e atenção.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Form */}
          <div className="bg-[#6E0C10]/50 border border-[#E3B24A]/15 rounded-lg p-8 md:p-10">
            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Nome Completo */}
              <div>
                <label
                  htmlFor="contact-name"
                  className="block font-body text-[#F2D27A] text-sm mb-2 font-medium"
                >
                  Nome Completo *
                </label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Seu nome completo"
                  className="w-full bg-[#550A0D]/50 border border-[#E3B24A]/20 text-white placeholder-white/30 font-body text-sm px-4 py-3 rounded-md focus:outline-none focus:border-[#E3B24A]/60 transition-colors"
                />
              </div>

              {/* E-mail */}
              <div>
                <label
                  htmlFor="contact-email"
                  className="block font-body text-[#F2D27A] text-sm mb-2 font-medium"
                >
                  E-mail *
                </label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="seu@email.com"
                  className="w-full bg-[#550A0D]/50 border border-[#E3B24A]/20 text-white placeholder-white/30 font-body text-sm px-4 py-3 rounded-md focus:outline-none focus:border-[#E3B24A]/60 transition-colors"
                />
              </div>

              {/* Área do Caso (select) */}
              <div>
                <label
                  htmlFor="contact-area"
                  className="block font-body text-[#F2D27A] text-sm mb-2 font-medium"
                >
                  Área do Caso *
                </label>
                <div className="relative">
                  <select
                    id="contact-area"
                    name="area"
                    required
                    value={formData.area}
                    onChange={handleChange}
                    className="w-full appearance-none bg-[#550A0D]/50 border border-[#E3B24A]/20 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-[#E3B24A]/60 transition-colors cursor-pointer font-body"
                    style={{ color: formData.area === "" ? "rgba(255,255,255,0.3)" : "white" }}
                  >
                    <option value="" disabled style={{ color: "rgba(255,255,255,0.4)", background: "#550A0D" }}>
                      Selecione a área do seu caso
                    </option>
                    {AREAS.map((area) => (
                      <option
                        key={area}
                        value={area}
                        style={{ color: "white", background: "#550A0D" }}
                      >
                        {area}
                      </option>
                    ))}
                  </select>
                  {/* Ícone chevron */}
                  <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#E3B24A]/60">
                    <i className="ri-arrow-down-s-line text-lg" />
                  </div>
                </div>
              </div>

              {/* Mensagem */}
              <div>
                <label
                  htmlFor="contact-message"
                  className="block font-body text-[#F2D27A] text-sm mb-2 font-medium"
                >
                  Mensagem *
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Descreva brevemente sua situação ou dúvida jurídica..."
                  className="w-full bg-[#550A0D]/50 border border-[#E3B24A]/20 text-white placeholder-white/30 font-body text-sm px-4 py-3 rounded-md focus:outline-none focus:border-[#E3B24A]/60 transition-colors resize-none"
                />
                <div className="flex justify-between mt-1">
                  <span
                    className={`font-body text-xs ${
                      charCount > 450 ? "text-red-400" : "text-[#F2D27A]/40"
                    }`}
                  >
                    {charCount}/500 caracteres
                  </span>
                  {charCount >= 500 && (
                    <span className="text-red-400 text-xs font-body">Limite atingido</span>
                  )}
                </div>
              </div>

              {/* Botão Enviar → abre WhatsApp */}
              <button
                type="submit"
                disabled={charCount > 500}
                className="w-full flex items-center justify-center gap-3 bg-[#E3B24A] hover:bg-[#F2D27A] disabled:opacity-60 text-[#6E0C10] font-body font-semibold text-sm py-4 rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg" />
                Enviar pelo WhatsApp
              </button>

              <p className="text-center font-body text-[#F2D27A]/35 text-xs">
                Ao clicar, você será direcionado ao WhatsApp com a mensagem já preenchida.
              </p>
            </form>
          </div>

          {/* Right: Info + Map */}
          <div className="flex flex-col gap-8">
            {/* Contact Info */}
            <div className="space-y-6">
              <a
                href={WHATSAPP_DIRECT}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#E3B24A]/15 border border-[#E3B24A]/30 group-hover:bg-[#E3B24A]/25 transition-colors">
                  <i className="ri-whatsapp-line text-[#E3B24A] text-xl" />
                </div>
                <div>
                  <p className="font-body text-[#F2D27A]/50 text-xs uppercase tracking-wider mb-0.5">WhatsApp</p>
                  <p className="font-body text-white font-medium text-base group-hover:text-[#E3B24A] transition-colors">
                    (47) 9 9930-0330
                  </p>
                </div>
              </a>

              <a
                href="mailto:lupepsaemarconatto.adv@gmail.com"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#E3B24A]/15 border border-[#E3B24A]/30 group-hover:bg-[#E3B24A]/25 transition-colors">
                  <i className="ri-mail-line text-[#E3B24A] text-xl" />
                </div>
                <div>
                  <p className="font-body text-[#F2D27A]/50 text-xs uppercase tracking-wider mb-0.5">E-mail</p>
                  <p className="font-body text-white font-medium text-sm group-hover:text-[#E3B24A] transition-colors">
                    lupepsaemarconatto.adv@gmail.com
                  </p>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#E3B24A]/15 border border-[#E3B24A]/30 flex-shrink-0">
                  <i className="ri-map-pin-2-line text-[#E3B24A] text-xl" />
                </div>
                <div>
                  <p className="font-body text-[#F2D27A]/50 text-xs uppercase tracking-wider mb-0.5">Endereço</p>
                  <p className="font-body text-white font-medium text-sm leading-relaxed">
                    Rua Amazonas, 2850 – Sala 14<br />
                    Garcia – Blumenau/SC
                  </p>
                </div>
              </div>

              <a
                href="https://instagram.com/lupepsaemarconattoadv"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#E3B24A]/15 border border-[#E3B24A]/30 group-hover:bg-[#E3B24A]/25 transition-colors">
                  <i className="ri-instagram-line text-[#E3B24A] text-xl" />
                </div>
                <div>
                  <p className="font-body text-[#F2D27A]/50 text-xs uppercase tracking-wider mb-0.5">Instagram</p>
                  <p className="font-body text-white font-medium text-sm group-hover:text-[#E3B24A] transition-colors">
                    @lupepsaemarconattoadv
                  </p>
                </div>
              </a>
            </div>

            {/* Google Maps */}
            <div className="rounded-lg overflow-hidden border border-[#E3B24A]/20 flex-1 min-h-[240px]">
              <iframe
                src="https://maps.google.com/maps?q=Rua+Amazonas+2850+Garcia+Blumenau+SC+Brasil&output=embed"
                title="Localização Lupepsa & Marconatto"
                className="w-full h-full min-h-[240px]"
                style={{ border: 0, filter: "sepia(30%) saturate(80%)" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
